import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  CreditCard,
  Landmark,
  ListChecks,
  Play,
  Settings,
} from "lucide-react";
import { generatePayPeriods } from "../../utils/payScheduling";
import CompanyProfileBanner from "../../components/company/CompanyProfileBanner";

import { C, FONT } from "./styles";
import {
  ACTIONS_CATALOG,
  ACTION_ICONS,
  FAVOURITES_STORAGE,
  SCHEDULE_LABELS,
} from "./constants";
import { formatPayDate, formatShortDate, money } from "./helpers";
import { useFavourites } from "./useFavourites";
import {
  useDraftsCount,
  useOverviewData,
  usePd7aData,
} from "./usePayrollOverview";
import type {
  AttentionItem as AttentionItemType,
  BankState,
  Employee,
  NextPayday,
  PayRun,
  PaySchedule,
  Readiness,
} from "./types";

import AttentionItem from "./components/AttentionItem";
import ChipButton from "./components/ChipButton";
import ResourceLink from "./components/ResourceLink";
import CreateActionsPanel from "./components/CreateActionsPanel";
import BankConnectPanel from "./components/BankConnectPanel";
import PayrollGuideSheet from "./components/PayrollGuideSheet";
import ThingsYouNeedPanel from "./components/ThingsYouNeedPanel";
import SettingUpPayrollPanel from "./components/SettingUpPayrollPanel";

function RunLine({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "9px 0",
        borderBottom: "1px solid " + C.lineSoft,
        fontSize: 13,
      }}
    >
      <span style={{ color: C.muted }}>{k}</span>
      <span
        style={{
          color: C.ink,
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {v}
      </span>
    </div>
  );
}

export default function PayrollOverview() {
  const navigate = useNavigate();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [paySchedule, setPaySchedule] = useState<PaySchedule | null>(null);
  const [lastRun, setLastRun] = useState<PayRun | null>(null);
  const [autoPayroll, setAutoPayroll] = useState(false);
  const [draftsCount, setDraftsCount] = useState(0);
  const [attentionItems, setAttentionItems] = useState<AttentionItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [attentionCollapsed, setAttentionCollapsed] = useState(false);
  const { favourites, save: saveFavourites } = useFavourites();
  const [showActions, setShowActions] = useState(false);
  const [bankState] = useState<BankState>("none");
  const [showBankConnect, setShowBankConnect] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showThingsNeeded, setShowThingsNeeded] = useState(false);
  const [showSettingUp, setShowSettingUp] = useState(false);

  const { data: draftsData } = useDraftsCount();
  const { data: overviewData, isLoading: overviewLoading } = useOverviewData();
  const { data: pd7aData } = usePd7aData();

  useEffect(() => {
    if (draftsData) setDraftsCount(draftsData.length);
  }, [draftsData]);

  useEffect(() => {
    if (overviewData) {
      setEmployees(overviewData.empData);
      setPaySchedule(overviewData.schedule);
      if (overviewData.finalizedRun) setLastRun(overviewData.finalizedRun);
    }
    setLoading(overviewLoading);
    setAutoPayroll(localStorage.getItem("novala_auto_payroll") === "true");
  }, [overviewData, overviewLoading]);

  useEffect(() => {
    if (!pd7aData) {
      setAttentionItems([]);
      return;
    }
    const amount = Number(pd7aData.current_payment || 0);
    if (amount <= 0) {
      setAttentionItems([]);
      return;
    }
    const dueDateStr = pd7aData.due_date || null;
    let dueDate: Date | null = null;
    if (dueDateStr) {
      dueDate = new Date(
        dueDateStr + (dueDateStr.length === 10 ? "T00:00:00" : "")
      );
    }
    const daysUntil = dueDate
      ? Math.floor((dueDate.getTime() - Date.now()) / 86400000)
      : null;
    let urgency: AttentionItemType["urgency"] = "normal";
    if (daysUntil !== null) {
      if (daysUntil < 0) urgency = "overdue";
      else if (daysUntil <= 2) urgency = "urgent";
      else if (daysUntil <= 7) urgency = "approaching";
    }
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    const monthName = monthNames[pd7aData._month - 1];
    const dueDisplay = dueDate
      ? dueDate.toLocaleDateString("en-CA", {
          weekday: "short",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
      : "";
    setAttentionItems([
      {
        id: "fed_tax",
        icon: urgency === "urgent" || urgency === "overdue" ? "red" : "amber",
        title: "Pay federal taxes for " + monthName + " " + pd7aData._year,
        amount,
        dueDate: dueDisplay,
        daysUntil,
        urgency,
        action: urgency === "urgent" || urgency === "overdue" ? "Pay now" : "Pay",
        onClick: () => navigate("/payroll/taxes/payments"),
      },
    ]);
  }, [pd7aData, navigate]);

  const nextPayday = useMemo<NextPayday>(() => {
    if (!paySchedule) return { date: null, daysUntil: null };
        const periods = generatePayPeriods(paySchedule as any, 3);
    if (!periods || periods.length === 0) return { date: null, daysUntil: null };
    const next = periods[0];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return {
      date: next.payDate,
      daysUntil: Math.floor(
        (next.payDate.getTime() - today.getTime()) / 86400000
      ),
      periodStart: next.periodStart,
      periodEnd: next.periodEnd,
    };
  }, [paySchedule]);

  const readiness = useMemo<Readiness>(() => {
    const total = employees.length;
    const ready = employees.filter((e) => e.profile_completed === true).length;
    return {
      total,
      ready,
      allReady: total > 0 && ready === total,
      firstUnsetup: employees.find((e) => !e.profile_completed),
    };
  }, [employees]);

  const allAttentionItems = useMemo<AttentionItemType[]>(() => {
    const items = [...attentionItems];
    employees.forEach((e) => {
      if (!e.profile_completed) {
        const name =
          (e.last_name ? e.last_name + ", " : "") + (e.first_name || "Employee");
        items.push({
          id: "setup_" + e.id,
          icon: "amber",
          title: name + " needs setup",
          desc: "Complete profile before next payday",
          action: "Fix",
          onClick: () => navigate("/payroll/employees/" + e.id),
        });
      }
    });
    return items;
  }, [attentionItems, employees, navigate]);

  const markDone = (id: string) =>
    setAttentionItems((items) => items.filter((i) => i.id !== id));

  const toggleAutoPayroll = () => {
    const v = !autoPayroll;
    setAutoPayroll(v);
    localStorage.setItem("novala_auto_payroll", v ? "true" : "false");
  };

  let headline = "Loading...";
  let isOverdue = false;
  if (!loading) {
    if (nextPayday.daysUntil === null) {
      headline = "Set up a pay schedule to see your next payday";
    } else if (nextPayday.daysUntil < 0) {
      headline =
        Math.abs(nextPayday.daysUntil) +
        " day" +
        (Math.abs(nextPayday.daysUntil) === 1 ? "" : "s") +
        " overdue";
      isOverdue = true;
    } else if (nextPayday.daysUntil === 0) {
      headline = "Payday is today";
    } else if (nextPayday.daysUntil === 1) {
      headline = "1 day until payday";
    } else {
      headline = nextPayday.daysUntil + " days until payday";
    }
  }
  const scheduleLabel = paySchedule
    ? SCHEDULE_LABELS[String(paySchedule.frequency)] || paySchedule.name || ""
    : "";

  return (
    <div style={{ background: C.surface, minHeight: "100vh", fontFamily: FONT, color: C.text }}>
      <div style={{ maxWidth: "100%", margin: 0, padding: "28px 32px 90px" }}>
        <CompanyProfileBanner />

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24, gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 34, fontWeight: 700, color: C.ink, letterSpacing: "-0.02em", margin: 0 }}>Payroll overview</h1>
            <div style={{ fontSize: 14, color: C.ink, fontWeight: 500, marginTop: 4 }}>Everything you need to run payroll, at a glance.</div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={() => navigate("/payroll/schedules")}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#fff", color: C.ink, border: "1.5px solid " + C.ink, borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: FONT, transition: "0.12s" }}
            >
              <Calendar size={15} /> Pay schedules
            </button>
            <button
              onClick={() => navigate("/payroll/settings")}
              style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#fff", color: C.ink, border: "1.5px solid " + C.ink, borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 12, cursor: "pointer", fontFamily: FONT, transition: "0.12s" }}
            >
              <Settings size={15} /> Payroll settings
            </button>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 22 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: C.muted, marginRight: 4 }}>Create actions</span>
          {favourites.slice(0, 5).map((favId) => {
            const action = ACTIONS_CATALOG.find((a) => a.id === favId);
            if (!action) return null;
            const Icon = ACTION_ICONS[favId] || Settings;
            return (
              <ChipButton
                key={favId}
                icon={<Icon size={15} />}
                label={action.label}
                onClick={() => navigate(action.route)}
              />
            );
          })}
          <span
            onClick={() => setShowActions(true)}
            style={{ marginLeft: "auto", fontSize: 13.5, fontWeight: 600, color: C.tealInk, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4, fontFamily: FONT }}
          >
            Show all <ChevronRight size={14} />
          </span>
        </div>

        {nextPayday.daysUntil !== null ? (
          <div style={{ background: "linear-gradient(135deg, #EAF8F4, #F1F8F6)", border: "1px solid #D5EDE6", borderRadius: 18, padding: "28px 30px", marginBottom: 22, position: "relative", overflow: "hidden" }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.tealInk, marginBottom: 8 }}>Next payday</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 34, fontWeight: 600, color: isOverdue ? C.red : C.ink, letterSpacing: "-0.02em", lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{headline}</div>
                {nextPayday.date && (
                  <div style={{ fontSize: 14, color: C.muted, marginTop: 6 }}>
                    <b style={{ color: C.ink, fontWeight: 600 }}>{formatPayDate(nextPayday.date)}</b> &middot; {scheduleLabel} schedule
                  </div>
                )}
                {nextPayday.periodStart && nextPayday.periodEnd && (
                  <div style={{ fontSize: 14, color: C.muted, marginTop: 4 }}>
                    Pay period <b style={{ color: C.ink, fontWeight: 600 }}>{formatShortDate(nextPayday.periodStart)}</b> to <b style={{ color: C.ink, fontWeight: 600 }}>{formatShortDate(nextPayday.periodEnd)}</b>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 10 }}>
                <button
                  onClick={() => navigate("/payroll/run")}
                  style={{ background: C.teal, color: "#fff", padding: "12px 22px", border: "none", borderRadius: 11, fontWeight: 600, fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 9, fontFamily: FONT, boxShadow: "0 2px 8px rgba(21,160,140,0.28)" }}
                >
                  <Play size={17} /> Run payroll
                </button>
                <button
                  onClick={() => navigate("/payroll/paycheques")}
                  style={{ background: "none", border: "none", fontSize: 13.5, fontWeight: 600, color: C.tealInk, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT }}
                >
                  Paycheque list <ChevronRight size={14} />
                </button>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 13, background: "#fff", border: "1px solid #D5EDE6", borderRadius: 13, padding: "14px 16px", marginTop: 22 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, display: "grid", placeItems: "center", flex: "0 0 38px", background: readiness.allReady ? C.greenSoft : C.amberSoft, color: readiness.allReady ? C.green : C.amber }}>
                {readiness.allReady ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
                  {readiness.ready} of {readiness.total} employee{readiness.total === 1 ? "" : "s"} ready for this run
                </div>
                {!readiness.allReady && readiness.firstUnsetup && (
                  <div style={{ fontSize: 13, color: C.muted }}>
                    {readiness.total - readiness.ready} need{readiness.total - readiness.ready === 1 ? "s" : ""} setup before payday
                  </div>
                )}
              </div>
              {!readiness.allReady && readiness.firstUnsetup && (
                <button
                  onClick={() => navigate("/payroll/employees/" + readiness.firstUnsetup!.id)}
                  style={{ background: "none", border: "none", fontSize: 13.5, fontWeight: 600, color: C.tealInk, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT }}
                >
                  Fix {(readiness.firstUnsetup.first_name || "employee").split(" ")[0]} <ChevronRight size={14} />
                </button>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 13, background: "#fff", border: "1px solid #D5EDE6", borderRadius: 13, padding: "14px 16px", marginTop: 14 }}>
              <div style={{ width: 38, height: 38, borderRadius: 10, display: "grid", placeItems: "center", flex: "0 0 38px", background: bankState === "ok" ? C.greenSoft : bankState === "pend" ? C.amberSoft : "#EEF1F5", color: bankState === "ok" ? C.green : bankState === "pend" ? C.amber : C.muted }}>
                <Landmark size={20} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink, display: "flex", alignItems: "center", gap: 8 }}>
                  {bankState === "ok" ? "Bank account connected" : bankState === "pend" ? "Verifying your bank account" : "Connect a bank account to pay by direct deposit"}
                  <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: bankState === "ok" ? C.greenSoft : "#EEF1F5", color: bankState === "ok" ? C.green : C.muted }}>
                    {bankState === "ok" ? "Direct deposit on" : bankState === "pend" ? "Direct deposit pending" : "Direct deposit off"}
                  </span>
                </div>
                <div style={{ fontSize: 12.5, color: C.muted }}>
                  {bankState === "ok"
                    ? "Employees can be paid by direct deposit."
                    : bankState === "pend"
                    ? "Watch for a small deposit, then confirm the amount to finish setup. You can still pay by cheque meanwhile."
                    : "Until a bank is connected and verified, pay employees by cheque."}
                </div>
              </div>
              <button
                onClick={() => navigate("/payroll/bank/connect")}
                style={{ background: "#12262B", color: "#fff", border: "none", borderRadius: 10, padding: "10px 18px", fontWeight: 600, fontSize: 13.5, cursor: "pointer", fontFamily: FONT, display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "0 2px 8px rgba(18,38,43,0.22)" }}
              >
                <Landmark size={14} /> {bankState === "ok" ? "Manage" : bankState === "pend" ? "Confirm" : "Connect bank"}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ background: "linear-gradient(135deg, #EAF8F4, #F1F8F6)", border: "1px solid #D5EDE6", borderRadius: 18, padding: "28px 30px", marginBottom: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.tealInk, marginBottom: 8 }}>Pay schedule</div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 26, fontWeight: 600, color: C.ink, letterSpacing: "-0.02em", lineHeight: 1.2 }}>Set up a pay schedule to see your next payday</div>
                <div style={{ fontSize: 14, color: C.muted, marginTop: 6 }}>Choose how often you pay, and Novala will count down to each payday.</div>
              </div>
              <button
                onClick={() => navigate("/payroll/settings")}
                style={{ background: C.teal, color: "#fff", padding: "12px 22px", border: "none", borderRadius: 11, fontWeight: 600, fontSize: 14, cursor: "pointer", fontFamily: FONT, boxShadow: "0 2px 8px rgba(21,160,140,0.28)" }}
              >
                Set up pay schedule
              </button>
            </div>
          </div>
        )}

        {draftsCount > 0 && (
          <div
            onClick={() => navigate("/payroll/drafts")}
            style={{ background: C.amberSoft, border: "1px solid #F4E0B0", borderRadius: 12, padding: "14px 18px", marginBottom: 22, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, cursor: "pointer" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "#F4E0B0", color: "#854F0B", display: "grid", placeItems: "center", fontSize: 18 }}>&#128221;</div>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.ink }}>
                  You have {draftsCount} draft pay run{draftsCount === 1 ? "" : "s"}
                </div>
                <div style={{ fontSize: 12, color: "#854F0B" }}>Auto-delete after 7 days</div>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate("/payroll/drafts");
              }}
              style={{ background: C.ink, color: "white", border: "none", padding: "9px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}
            >
              View drafts &rarr;
            </button>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 22, alignItems: "start" }}>
          <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 16, boxShadow: "0 1px 2px rgba(16,26,43,0.04)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "18px 22px", borderBottom: "1px solid " + C.lineSoft }}>
              <h2 style={{ fontSize: 16, fontWeight: 600, color: C.ink }}>Needs your attention</h2>
              <span style={{ fontSize: 11.5, fontWeight: 700, background: allAttentionItems.length > 0 ? C.amberSoft : C.greenSoft, color: allAttentionItems.length > 0 ? C.amber : C.green, borderRadius: 20, padding: "2px 9px" }}>
                {allAttentionItems.length} {allAttentionItems.length === 1 ? "item" : "items"}
              </span>
              <span
                onClick={() => setAttentionCollapsed((c) => !c)}
                style={{ marginLeft: "auto", cursor: "pointer", color: C.muted, display: "inline-flex", alignItems: "center", transform: attentionCollapsed ? "rotate(-90deg)" : "rotate(0deg)", transition: "transform 0.18s" }}
              >
                <ChevronDown size={18} />
              </span>
            </div>
            {!attentionCollapsed &&
              (allAttentionItems.length === 0 ? (
                <div style={{ padding: "30px 22px", textAlign: "center", color: C.muted, fontSize: 13.5 }}>
                  All clear. Nothing needs your attention right now.
                </div>
              ) : (
                allAttentionItems.map((item, idx) => (
                  <AttentionItem
                    key={item.id}
                    item={item}
                    isLast={idx === allAttentionItems.length - 1}
                    onMarkDone={() => markDone(item.id)}
                  />
                ))
              ))}
          </div>

          <div>
            <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 16, padding: 20, boxShadow: "0 1px 2px rgba(16,26,43,0.04)", marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <h3 style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>Auto Payroll</h3>
                <div
                  onClick={toggleAutoPayroll}
                  style={{ width: 42, height: 24, borderRadius: 20, background: autoPayroll ? C.teal : C.line, position: "relative", cursor: "pointer", transition: "0.18s", flex: "0 0 42px" }}
                >
                  <div style={{ position: "absolute", top: 2, left: autoPayroll ? 20 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "0.18s", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }} />
                </div>
              </div>
              <p style={{ fontSize: 13, color: C.muted, marginTop: 7 }}>Run your payroll automatically when everyone is ready.</p>
              <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: C.faint }}>{autoPayroll ? "On" : "Off"}</div>
            </div>

            <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 16, padding: 20, boxShadow: "0 1px 2px rgba(16,26,43,0.04)", marginBottom: 18 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>Last pay run</h3>
              {lastRun ? (
                <>
                  <div style={{ marginTop: 12 }}>
                    <RunLine k="Pay date" v={formatShortDate(new Date(lastRun.pay_date))} />
                    <RunLine k="Employees paid" v={lastRun.employee_count || 0} />
                    <RunLine k="Total cost" v={money(lastRun.total_gross)} />
                    <RunLine k="Net to employees" v={money(lastRun.total_net)} />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <button
                      onClick={() => navigate("/payroll/run/" + lastRun.id + "/done")}
                      style={{ background: "none", border: "none", fontSize: 13.5, fontWeight: 600, color: C.tealInk, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontFamily: FONT }}
                    >
                      View pay run <ChevronRight size={14} />
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ marginTop: 12, fontSize: 13, color: C.muted }}>
                  No pay runs yet. Run your first payroll to see it here.
                </div>
              )}
            </div>

            <div style={{ background: "#fff", border: "1px solid " + C.line, borderRadius: 16, padding: 20, boxShadow: "0 1px 2px rgba(16,26,43,0.04)" }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.ink }}>Setup resources</h3>
              <div style={{ marginTop: 8 }}>
                <ResourceLink icon={<Settings size={20} />} label="Payroll settings" onClick={() => navigate("/payroll/settings")} />
                <ResourceLink icon={<BookOpen size={20} />} label="View setup guide" onClick={() => setShowGuide(true)} />
                <ResourceLink icon={<ListChecks size={20} />} label="Things you will need" onClick={() => setShowThingsNeeded(true)} />
                <ResourceLink
                  icon={<Settings size={20} />}
                  label={paySchedule ? "Update payroll settings" : "Setting up payroll"}
                  mins={paySchedule ? undefined : "2 min"}
                  onClick={() => (paySchedule ? navigate("/payroll/settings") : setShowSettingUp(true))}
                />
                <ResourceLink
                  icon={<Play size={20} />}
                  label={lastRun ? "Run payroll" : "Running your first payroll"}
                  mins={lastRun ? undefined : "3 min"}
                  onClick={() => navigate("/payroll/run")}
                />
                <ResourceLink
                  icon={<CreditCard size={20} />}
                  label="Set up direct deposit"
                  onClick={() => navigate("/payroll/bank/connect")}
                  isLast
                />
              </div>
            </div>
          </div>
        </div>

        {showActions && (
          <CreateActionsPanel
            initialFavs={favourites}
            onSave={(newFavs) => {
              saveFavourites(newFavs);
              localStorage.setItem(FAVOURITES_STORAGE, JSON.stringify(newFavs));
              setShowActions(false);
            }}
            onClose={() => setShowActions(false)}
          />
        )}
        {showBankConnect && <BankConnectPanel onClose={() => setShowBankConnect(false)} />}
        {showGuide && <PayrollGuideSheet onClose={() => setShowGuide(false)} />}
        {showThingsNeeded && <ThingsYouNeedPanel onClose={() => setShowThingsNeeded(false)} />}
        {showSettingUp && (
          <SettingUpPayrollPanel
            onClose={() => setShowSettingUp(false)}
            onConnectBank={() => navigate("/payroll/bank/connect")}
          />
        )}
      </div>
    </div>
  );
}